import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/auth";
import { LHSLogo, BrandLogo } from "@/logos";
import { useTranslation } from "react-i18next";
import { setLanguage } from "@/i18n";
import {
  Download, Shield, Cpu, Zap, Microchip, Apple, Smartphone,
  Infinity as InfinityIcon, CheckCircle2, ArrowRight, KeyRound, Wallet,
  Copy, Youtube, Facebook, MessageCircle, Send, Mail, MapPin,
  ShieldCheck, Clock, Award, HeadphonesIcon, Globe, ChevronDown,
  Lock, HardDrive, Database, Coins,
} from "lucide-react";
import SupportChat from "@/components/SupportChat";
import { LicenseOrderForm } from "@/components/LicenseOrderForm";

// Public drivers section
function LandingPilotes() {
  const [drivers, setDrivers] = useState([]);
  useEffect(() => {
    axios.get(`${API}/drivers`).then(r => setDrivers(r.data || [])).catch(() => {});
  }, []);
  if (!drivers.length) return <div className="text-center text-zinc-500 text-sm">Chargement des pilotes…</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="landing-drivers-grid">
      {drivers.map(d => (
        <div key={d.id} data-testid={`landing-driver-${d.slug}`} className="card-lhs p-5 flex flex-col gap-3">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center">
              <HardDrive className="w-5 h-5 text-orange-500" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-extrabold text-sm leading-tight">{d.name}</div>
              <div className="text-[10px] text-zinc-500 font-mono-tech mt-0.5">
                v{d.version || "?"} · MàJ {d.updated_at && new Date(d.updated_at).toLocaleDateString("fr-FR")}
              </div>
            </div>
          </div>
          <div className="text-xs text-zinc-400 line-clamp-3 min-h-[2.5rem]">{d.description || "—"}</div>
          <a href={d.has_file ? `${API}/drivers/${d.id}/download` : undefined}
            data-testid={`landing-driver-dl-${d.slug}`}
            className={`w-full rounded-lg py-2 text-xs uppercase tracking-wider inline-flex items-center justify-center gap-2 ${
              d.has_file ? "btn-orange" : "border border-zinc-700 text-zinc-500 cursor-not-allowed"
            }`}
            onClick={e => { if (!d.has_file) e.preventDefault(); }}
          >
            <Download className="w-3.5 h-3.5" />
            {d.has_file ? "Télécharger" : "Bientôt disponible"}
          </a>
        </div>
      ))}
    </div>
  );
}

// ----- Static data -----
const PLATFORMS = [
  { id: "mediatek", icon: Cpu,        label: "MediaTek",  sub: "Helio · Dimensity · BROM · Preloader · META Mode" },
  { id: "qualcomm", icon: Zap,        label: "Qualcomm",  sub: "Snapdragon · EDL 9008 · Fastboot · Sahara" },
  { id: "unisoc",   icon: Microchip,  label: "Unisoc",    sub: "SPD Loader · Spreadtrum · SC9863A · T612" },
  { id: "infinity", icon: InfinityIcon,label:"Infinity",  sub: "Serveur d'authentification multi-marques" },
  { id: "ios",      icon: Apple,      label: "iOS Apple", sub: "DFU · Recovery · Hello Bypass · iCloud" },
  { id: "android",  icon: Smartphone, label: "Android",   sub: "ADB · Fastboot · Recovery · MDM · KG Lock" },
];

const BRANDS = [
  "apple","samsung","xiaomi","huawei","oppo","realme","oneplus","tecno",
  "infinix","itel","nokia","motorola","wiko","lg","zte","sony","pixel","vivo",
];

const FEATURES = [
  { icon: Cpu,          title: "Détection modèle exacte",   desc: "Marque + modèle + variante + CPU + mode + version. Fiches vérifiées par l'équipe LHS." },
  { icon: KeyRound,     title: "20+ opérations pro",         desc: "FRP, Mi Account, Knox, iCloud, MDM, KG Lock, MKOPA — procédures officielles." },
  { icon: Apple,        title: "iPhone Hello Bypass",        desc: "Retrait Activation Lock sur iPhones autorisés, whitelist admin, log serveur signé." },
  { icon: HardDrive,    title: "Base firmware & pilotes",    desc: "Firmwares officiels, pilotes USB par marque, checksum SHA-256 sur chaque fichier." },
  { icon: Shield,       title: "Sécurité de niveau bancaire",desc: "OTP + lien signé, CAPTCHA, binding machine 4h, JWT courts, base MongoDB isolée." },
  { icon: Wallet,       title: "Paiement local RDC",         desc: "Airtel Money · Orange Money · Binance Pay · Visa. Approbation admin en temps réel." },
  { icon: Database,     title: "Moteur de compatibilité",    desc: "Base admin-éditable Android 11→17+ / iOS 11→27+, mises à jour sans réinstallation." },
  { icon: Coins,        title: "Crédits & licences tierces", desc: "Achat de crédits (0.80 $/crédit) et catalogue UnlockTool, EFT, Sigma, MRT, etc." },
  { icon: HeadphonesIcon,title: "Support 24/7 francophone",  desc: "WhatsApp, Telegram, chaîne officielle et communauté active à Goma, RDC." },
];

const HOW = [
  { n: "01", title: "Créez votre compte", desc: "Inscription en 2 minutes avec double validation (code OTP + lien sécurisé)." },
  { n: "02", title: "Obtenez votre licence", desc: "3, 6 ou 12 mois — paiement Airtel Money, Orange Money ou Binance Pay." },
  { n: "03", title: "Activez et débloquez", desc: "L'administrateur valide votre paiement, votre licence s'active instantanément." },
];

const FAQ = [
  { q: "Quels sont les moyens de paiement acceptés ?",
    a: "Airtel Money, Orange Money, Binance Pay et Visa/carte bancaire. Le paiement est vérifié manuellement par l'administrateur pour garantir la sécurité." },
  { q: "Puis-je utiliser LHSunlocker sur plusieurs machines ?",
    a: "La licence est attachée à votre compte. Vous pouvez basculer d'une machine à une autre : fermez complètement l'application sur la machine A, puis reconnectez-vous sur la machine B." },
  { q: "Les fichiers firmware sont-ils légaux ?",
    a: "Nous ne distribuons que des firmwares dont la redistribution est autorisée par le constructeur. Chaque fichier est vérifié et signé SHA-256." },
  { q: "Combien de temps prend l'activation ?",
    a: "L'activation est manuelle mais rapide : généralement moins de 30 minutes en journée. Vous recevez un email de confirmation avec la preuve d'activation officielle." },
  { q: "Puis-je essayer avant d'acheter ?",
    a: "Créez un compte gratuit pour voir toute l'interface. Les fonctions de déblocage nécessitent une licence active." },
  { q: "Le logiciel fonctionne-t-il sur Mac ou Linux ?",
    a: "Le client desktop USB est Windows uniquement (7/8/10/11). Le portail web fonctionne sur tout navigateur moderne (desktop, tablette, téléphone)." },
];

const SOCIAL = {
  whatsapp: "+243 853 483 113",
  whatsapp_link: "https://wa.me/243853483113",
  telegram: "+243 990 145 542",
  telegram_link: "https://t.me/+YVf1T1LTk-1iZGI8",
  email: "lhsunlocker@gmail.com",
  channel: "https://whatsapp.com/channel/0029VbC6d2lGJP8MMhjeZK46",
  youtube: "https://www.youtube.com/@LHSunlocker",
  facebook: "https://www.facebook.com/profile.php?id=61594563128629",
  city: "Goma, République démocratique du Congo",
};

// ----- Sub-components -----
function TrustStrip() {
  return (
    <div className="border-y border-zinc-800/60 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
        {[
          { icon: Shield, label: "TLS 1.3 · JWT signé" },
          { icon: Clock,  label: "Disponibilité 24/7" },
          { icon: Award,  label: "Support francophone" },
          { icon: Globe,  label: "Basé à Goma, RDC" },
        ].map((it) => (
          <div key={it.label} className="flex items-center justify-center gap-2 text-zinc-400 text-xs sm:text-sm">
            <it.icon className="w-4 h-4 text-orange-500 shrink-0" />
            <span className="font-mono-tech">{it.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FaqItem({ q, a, i }) {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="card-lhs p-0 overflow-hidden" data-testid={`faq-${i}`}>
      <button onClick={() => setOpen(!open)}
        className="w-full text-left p-4 sm:p-5 flex items-center justify-between gap-3 hover:bg-zinc-900/40 transition">
        <span className="font-semibold text-sm sm:text-base">{q}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180 text-orange-400" : "text-zinc-500"}`} />
      </button>
      {open && <div className="px-4 sm:px-5 pb-4 sm:pb-5 text-sm text-zinc-400 leading-relaxed">{a}</div>}
    </div>
  );
}

// ----- Main -----
export default function LandingPage() {
  const [cfg, setCfg] = useState(null);
  const [copied, setCopied] = useState(false);
  const { i18n } = useTranslation();

  useEffect(() => {
    axios.get(`${API}/app/config`).then(r => setCfg(r.data)).catch(() => {});
  }, []);

  const dlUrl = cfg?.app_download_url
    ? (cfg.app_download_url.startsWith("http") ? cfg.app_download_url : `${API}${cfg.app_download_url.replace(/^\/api/, "")}`)
    : null;

  const copySha = () => {
    if (!cfg?.installer_sha256) return;
    navigator.clipboard.writeText(cfg.installer_sha256);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur bg-black/70 border-b border-zinc-800/60">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 gap-2">
          <Link to="/" className="shrink-0 flex items-center gap-3">
            <LHSLogo size={40} />
            <div className="hidden sm:block">
              <div className="font-display font-black text-sm leading-tight">LHSunlocker</div>
              <div className="text-[9px] uppercase tracking-widest text-orange-400 font-mono-tech">Serveur Officiel</div>
            </div>
          </Link>
          <nav className="hidden lg:flex items-center gap-6 text-sm text-zinc-400">
            <a href="#platforms" className="hover:text-orange-400 transition">Plateformes</a>
            <a href="#features" className="hover:text-orange-400 transition">Fonctionnalités</a>
            <a href="#pricing" className="hover:text-orange-400 transition">Tarifs</a>
            <a href="#how" className="hover:text-orange-400 transition">Comment ça marche</a>
            <a href="#faq" className="hover:text-orange-400 transition">FAQ</a>
            <a href="#contact" className="hover:text-orange-400 transition">Contact</a>
          </nav>
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="hidden sm:flex items-center gap-1 mr-2">
              {["fr","en","ar"].map(l => (
                <button key={l} data-testid={`landing-lang-${l}`} onClick={() => setLanguage(l)}
                  className={`text-[10px] font-mono-tech px-2 py-1 rounded border ${i18n.language === l ? "border-orange-500 text-orange-400" : "border-zinc-800 text-zinc-500 hover:text-white"}`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <Link data-testid="nav-login" to="/login" className="text-xs sm:text-sm text-zinc-400 hover:text-white px-2 sm:px-3 py-2 whitespace-nowrap">Connexion</Link>
            <Link data-testid="nav-register" to="/register" className="btn-orange rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm uppercase tracking-wider whitespace-nowrap">Compte</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-grid">
        <div className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(255,107,0,.2), transparent 62%)" }} />
        <div className="absolute -bottom-40 -right-40 w-[700px] h-[700px] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(255,107,0,.14), transparent 62%)" }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 brand-pill text-orange-400 mb-5">
              <span className="pulse-dot" /> PLATEFORME GSM PROFESSIONNELLE · GOMA
            </div>
            <h1 className="font-display font-black text-3xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight">
              Le serveur officiel <span className="text-orange-500">LHSunlocker</span> pour techniciens GSM
            </h1>
            <p className="text-zinc-400 mt-5 text-base sm:text-lg max-w-xl leading-relaxed">
              Détection USB, déblocage multi-plateformes, base firmware sécurisée, paiement mobile local
              et catalogue de licences tierces — tout dans une seule interface pensée pour les ateliers de réparation.
            </p>

            <div className="flex flex-wrap items-center gap-3 mt-7">
              <Link to="/register" data-testid="hero-register"
                className="btn-orange rounded-lg px-6 py-3 text-sm uppercase tracking-wider inline-flex items-center gap-2">
                Créer mon compte technicien <ArrowRight className="w-4 h-4" />
              </Link>
              {dlUrl && (
                <a href={dlUrl} data-testid="landing-download-btn"
                   className="rounded-lg px-6 py-3 text-sm uppercase tracking-wider border border-zinc-700 hover:border-orange-500 hover:text-orange-400 inline-flex items-center gap-2 transition">
                  <Download className="w-4 h-4" /> Télécharger le client Windows
                </a>
              )}
            </div>

            {cfg?.installer_sha256 && (
              <div className="mt-6 rounded-xl border border-zinc-800 bg-zinc-950/70 p-4 max-w-xl" data-testid="landing-sha">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  <div className="text-[11px] uppercase tracking-widest text-emerald-400 font-mono-tech">Vérification SHA-256 · v{cfg?.app_version || "1.0.0"}</div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <code className="text-[11px] sm:text-xs text-zinc-400 font-mono-tech break-all">{cfg.installer_sha256}</code>
                  <button onClick={copySha} className="text-zinc-400 hover:text-orange-400 shrink-0" title="Copier">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                {copied && <div className="text-[11px] text-emerald-400 mt-1">Copié ✓</div>}
                <div className="text-[11px] text-zinc-500 font-mono-tech mt-2">
                  Sur Windows : <code className="text-orange-400">certutil -hashfile "LHSunlocker Setup.exe" SHA256</code>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4 sm:gap-6 mt-7 text-[11px] text-zinc-500 font-mono-tech">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Windows 7/8/10/11</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Licence serveur temps réel</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Mise à jour automatique</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Support 24/7</span>
            </div>
          </div>

          {/* Hero visual */}
          <div className="relative">
            <div className="card-lhs p-6 sm:p-7 relative overflow-hidden">
              <div className="flex justify-center mb-5"><LHSLogo size={100} /></div>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {PLATFORMS.map(p => (
                  <div key={p.id} className="card-lhs !rounded-xl p-3 flex flex-col items-center gap-2 aspect-square">
                    <p.icon className="w-6 h-6 text-orange-500" />
                    <span className="text-[10px] sm:text-[11px] font-mono-tech text-zinc-300 text-center">{p.label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 rounded-lg border border-orange-500/30 bg-orange-500/5 p-3 text-center">
                <div className="text-[10px] uppercase tracking-widest text-orange-400 font-mono-tech">Fast · Reliable · Professional</div>
                <div className="text-lg font-display font-extrabold mt-1">Unlock Tool GSM · Édition Serveur</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <TrustStrip />

      {/* Platforms */}
      <section id="platforms" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-12">
          <div className="text-xs uppercase tracking-widest text-orange-400 font-mono-tech mb-2">Plateformes prises en charge</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl">Un serveur, toutes les architectures</h2>
          <p className="text-zinc-400 mt-3 text-sm sm:text-base">
            Chaque chipset dispose de son propre moteur de commandes, de ses pilotes et de ses procédures documentées.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {PLATFORMS.map(p => (
            <div key={p.id} className="card-lhs p-5 sm:p-6 hover:orange-glow transition" data-testid={`landing-platform-${p.id}`}>
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mb-4">
                <p.icon className="w-6 h-6 text-orange-500" />
              </div>
              <div className="font-display font-extrabold text-lg">{p.label}</div>
              <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{p.sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-y border-zinc-800/60 bg-[#09090B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
            <div className="text-xs uppercase tracking-widest text-orange-400 font-mono-tech mb-2">Fonctionnalités clés</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl">Tout ce qu'il faut à un atelier moderne</h2>
            <p className="text-zinc-400 mt-3 text-sm sm:text-base">Interface professionnelle, base de compatibilité admin-éditable, serveur qui valide vos licences et crédits en temps réel.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {FEATURES.map(f => (
              <div key={f.title} className="card-lhs p-5 sm:p-6" data-testid={`feature-${f.title.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="w-11 h-11 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center orange-glow">
                  <f.icon className="w-5 h-5 text-orange-500" />
                </div>
                <h3 className="font-display font-extrabold text-lg mt-4">{f.title}</h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Drivers - public download */}
      <section id="pilotes" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20" data-testid="landing-pilotes">
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
          <div className="text-xs uppercase tracking-widest text-orange-400 font-mono-tech mb-2">Pilotes USB · Téléchargement public</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl">Pilotes USB officiels</h2>
          <p className="text-zinc-400 mt-3 text-sm sm:text-base">
            Téléchargez les pilotes USB nécessaires à LHSunlocker Desktop pour communiquer avec vos téléphones.
            Aucun compte requis.
          </p>
        </div>
        <LandingPilotes />
      </section>

      {/* Brands */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-widest text-orange-400 font-mono-tech mb-2">18 marques référencées · évolutif</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl">Compatible avec tous les grands fabricants</h2>
          <p className="text-zinc-400 mt-3 text-sm">Base de compatibilité par modèle exact — pas seulement par marque.</p>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-9 gap-2 sm:gap-3">
          {BRANDS.map(b => (
            <div key={b} className="card-lhs !rounded-xl p-3 flex flex-col items-center gap-2 aspect-square">
              <BrandLogo id={b} size={24} />
              <span className="text-[10px] text-zinc-400 font-mono-tech capitalize">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-y border-zinc-800/60 bg-[#09090B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-12">
            <div className="text-xs uppercase tracking-widest text-orange-400 font-mono-tech mb-2">Licences</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl">Obtenez votre licence</h2>
            <p className="text-zinc-400 mt-3 text-sm sm:text-base">Paiement local : Airtel Money · Orange Money · Binance Pay · Visa</p>
          </div>
          <div className="card-lhs p-6 sm:p-10 max-w-3xl mx-auto text-center" data-testid="landing-pricing-contact">
            <p className="text-zinc-300 text-sm sm:text-base">
              Licences de 3, 6 ou 12 mois. Le prix et les instructions de paiement vous sont communiqués
              directement par l'administrateur.
            </p>
            <div className="mt-6"><LicenseOrderForm /></div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
          <div className="text-xs uppercase tracking-widest text-orange-400 font-mono-tech mb-2">Comment ça marche</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl">3 étapes pour démarrer</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
          {HOW.map(s => (
            <div key={s.n} className="card-lhs p-5 sm:p-6 relative overflow-hidden">
              <div className="text-6xl sm:text-7xl font-display font-black text-orange-500/10 absolute -top-2 right-3">{s.n}</div>
              <div className="relative">
                <div className="font-mono-tech text-xs text-orange-400">ÉTAPE {s.n}</div>
                <h3 className="font-display font-extrabold text-xl mt-2">{s.title}</h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-y border-zinc-800/60 bg-[#09090B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-widest text-orange-400 font-mono-tech mb-2">Questions fréquentes</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl">Vous avez des questions ?</h2>
          </div>
          <div className="space-y-3">
            {FAQ.map((f, i) => <FaqItem key={f.q} i={i} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-12">
          <div className="text-xs uppercase tracking-widest text-orange-400 font-mono-tech mb-2">Support technique</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl">Contactez l'équipe LHSunlocker</h2>
          <p className="text-zinc-400 mt-3 text-sm sm:text-base">Assistance francophone · Basée à Goma, RDC · Réponse rapide sur WhatsApp</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          <a href={SOCIAL.whatsapp_link} target="_blank" rel="noreferrer"
             className="card-lhs p-5 sm:p-6 hover:orange-glow transition flex items-start gap-4" data-testid="contact-whatsapp">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
              <MessageCircle className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <div className="font-display font-extrabold">WhatsApp</div>
              <div className="text-sm text-zinc-400 mt-1 font-mono-tech">{SOCIAL.whatsapp}</div>
              <div className="text-[11px] text-emerald-400 font-mono-tech mt-2">→ Ouvrir la conversation</div>
            </div>
          </a>

          <a href={SOCIAL.channel} target="_blank" rel="noreferrer"
             className="card-lhs p-5 sm:p-6 hover:orange-glow transition flex items-start gap-4" data-testid="contact-channel">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
              <MessageCircle className="w-6 h-6 text-emerald-400" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-extrabold">Chaîne WhatsApp</div>
              <div className="text-sm text-zinc-400 mt-1">Annonces officielles</div>
              <div className="text-[11px] text-emerald-400 font-mono-tech mt-2">→ S'abonner à la chaîne</div>
            </div>
          </a>

          <a href={SOCIAL.telegram_link} target="_blank" rel="noreferrer"
             className="card-lhs p-5 sm:p-6 hover:orange-glow transition flex items-start gap-4" data-testid="contact-telegram">
            <div className="w-12 h-12 rounded-xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center shrink-0">
              <Send className="w-6 h-6 text-sky-400" />
            </div>
            <div>
              <div className="font-display font-extrabold">Telegram</div>
              <div className="text-sm text-zinc-400 mt-1 font-mono-tech">{SOCIAL.telegram}</div>
              <div className="text-[11px] text-sky-400 font-mono-tech mt-2">→ Rejoindre le groupe</div>
            </div>
          </a>

          <a href={`mailto:${SOCIAL.email}`}
             className="card-lhs p-5 sm:p-6 hover:orange-glow transition flex items-start gap-4" data-testid="contact-email">
            <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center shrink-0">
              <Mail className="w-6 h-6 text-orange-500" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-extrabold">Email officiel</div>
              <div className="text-sm text-zinc-400 mt-1 font-mono-tech truncate">{SOCIAL.email}</div>
              <div className="text-[11px] text-orange-400 font-mono-tech mt-2">→ Envoyer un email</div>
            </div>
          </a>

          <a href={SOCIAL.youtube} target="_blank" rel="noreferrer"
             className="card-lhs p-5 sm:p-6 hover:orange-glow transition flex items-start gap-4" data-testid="contact-youtube">
            <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center shrink-0">
              <Youtube className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <div className="font-display font-extrabold">YouTube</div>
              <div className="text-sm text-zinc-400 mt-1">Tutoriels vidéo</div>
              <div className="text-[11px] text-red-400 font-mono-tech mt-2">→ @LHSunlocker</div>
            </div>
          </a>

          <a href={SOCIAL.facebook} target="_blank" rel="noreferrer"
             className="card-lhs p-5 sm:p-6 hover:orange-glow transition flex items-start gap-4" data-testid="contact-facebook">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center shrink-0">
              <Facebook className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <div className="font-display font-extrabold">Facebook</div>
              <div className="text-sm text-zinc-400 mt-1">Page officielle</div>
              <div className="text-[11px] text-blue-400 font-mono-tech mt-2">→ Suivre la page</div>
            </div>
          </a>
        </div>

        <div className="mt-8 flex items-center justify-center gap-2 text-sm text-zinc-500">
          <MapPin className="w-4 h-4 text-orange-500" /> {SOCIAL.city}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20">
        <div className="card-lhs p-8 sm:p-12 relative overflow-hidden text-center">
          <div className="absolute inset-0 pointer-events-none"
               style={{ background: "radial-gradient(circle at center, rgba(255,107,0,.15), transparent 60%)" }} />
          <div className="relative">
            <h2 className="font-display font-black text-3xl sm:text-4xl">Prêt à débloquer plus vite ?</h2>
            <p className="text-zinc-400 mt-4 max-w-xl mx-auto text-sm sm:text-base">
              Créez votre compte technicien, choisissez une licence et lancez votre première opération
              dans les minutes qui suivent.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 mt-8">
              <Link to="/register" data-testid="final-register"
                className="btn-orange rounded-lg px-6 py-3 text-sm uppercase tracking-wider inline-flex items-center justify-center gap-2">
                Créer mon compte technicien <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" data-testid="final-login"
                className="rounded-lg px-6 py-3 text-sm uppercase tracking-wider border border-zinc-700 hover:border-orange-500 hover:text-orange-400 transition text-center">
                J'ai déjà un compte
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800/60 bg-[#050508]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <LHSLogo size={44} />
              <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
                Plateforme officielle de déblocage GSM pour techniciens professionnels.
                Basée à Goma, République démocratique du Congo.
              </p>
            </div>
            <div>
              <div className="font-display font-extrabold text-sm mb-3">Plateforme</div>
              <ul className="space-y-2 text-xs text-zinc-500">
                <li><a href="#platforms" className="hover:text-orange-400">Plateformes</a></li>
                <li><a href="#features" className="hover:text-orange-400">Fonctionnalités</a></li>
                <li><a href="#pricing" className="hover:text-orange-400">Tarifs</a></li>
                <li><a href="#faq" className="hover:text-orange-400">FAQ</a></li>
              </ul>
            </div>
            <div>
              <div className="font-display font-extrabold text-sm mb-3">Compte</div>
              <ul className="space-y-2 text-xs text-zinc-500">
                <li><Link to="/login" className="hover:text-orange-400">Connexion</Link></li>
                <li><Link to="/register" className="hover:text-orange-400">Créer un compte</Link></li>
                <li><Link to="/forgot" className="hover:text-orange-400">Mot de passe oublié</Link></li>
              </ul>
            </div>
            <div>
              <div className="font-display font-extrabold text-sm mb-3">Contact</div>
              <ul className="space-y-2 text-xs text-zinc-500">
                <li><a href={SOCIAL.whatsapp_link} target="_blank" rel="noreferrer" className="hover:text-orange-400">WhatsApp {SOCIAL.whatsapp}</a></li>
                <li><a href={SOCIAL.telegram_link} target="_blank" rel="noreferrer" className="hover:text-orange-400">Telegram {SOCIAL.telegram}</a></li>
                <li><a href={`mailto:${SOCIAL.email}`} className="hover:text-orange-400">{SOCIAL.email}</a></li>
                <li className="text-zinc-600">{SOCIAL.city}</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-6 border-t border-zinc-800/60">
            <span className="text-[11px] text-zinc-500 font-mono-tech text-center sm:text-left">
              © {new Date().getFullYear()} LHSunlocker · Plateforme GSM Professionnelle · Tous droits réservés
            </span>
            <div className="flex items-center gap-4 text-zinc-500">
              <a href={SOCIAL.youtube} target="_blank" rel="noreferrer" title="YouTube" className="hover:text-red-400"><Youtube className="w-5 h-5" /></a>
              <a href={SOCIAL.facebook} target="_blank" rel="noreferrer" title="Facebook" className="hover:text-blue-400"><Facebook className="w-5 h-5" /></a>
              <a href={SOCIAL.whatsapp_link} target="_blank" rel="noreferrer" title="WhatsApp" className="hover:text-emerald-400"><MessageCircle className="w-5 h-5" /></a>
              <a href={SOCIAL.telegram_link} target="_blank" rel="noreferrer" title="Telegram" className="hover:text-sky-400"><Send className="w-5 h-5" /></a>
              <span className="text-[11px] font-mono-tech">v{cfg?.app_version || "1.0.0"}</span>
            </div>
          </div>
        </div>
      </footer>

      <SupportChat />
    </div>
  );
}
